import type { IBodyAddBlockHoursBatchItem } from 'services/types/blockHours';
import type { ITimeOffEntry } from 'TimeOffList/services/types';
import { fmtShort, fmtTime } from 'TimeOffList/services/format';

export const DEFAULT_FROM_MIN = 9 * 60; // 09:00
export const DEFAULT_TO_MIN = 20 * 60; // 20:00

// A record staged into the Selected panel (one per staff, before commit).
export interface IStagedRecord {
  id: number; // local staging id
  staffId: number;
  name: string;
  start: string; // 'YYYY-MM-DD'
  end: string;
  allDay: boolean;
  fromMin?: number;
  toMin?: number;
  reason: string;
}

export interface OverlapDetail {
  label: string;
}

export interface FormState {
  selectedStaffIds: number[];
  rangeStart: string | null;
  rangeEnd: string | null;
  allDay: boolean;
  fromMin: number;
  toMin: number;
}

const overlapDays = (aS: string, aE: string, bS: string, bE: string) => aS <= bE && bS <= aE;

/**
 * Non-blocking conflict detection: for each selected staff, list existing
 * entries whose date range intersects the candidate range AND whose time
 * window clashes (either side all-day, or the minute windows intersect).
 */
export const checkOverlaps = (form: FormState, existing: ITimeOffEntry[]): OverlapDetail[] => {
  const { selectedStaffIds, rangeStart, rangeEnd, allDay, fromMin, toMin } = form;
  if (!selectedStaffIds.length || !rangeStart || !rangeEnd) return [];

  const details: OverlapDetail[] = [];
  selectedStaffIds.forEach(staffId => {
    existing.forEach(h => {
      if (h.staffId !== staffId) return;
      if (!overlapDays(rangeStart, rangeEnd, h.start, h.end)) return;
      const hFrom = h.fromMin ?? 0;
      const hTo = h.toMin ?? 24 * 60;
      const timeClash = allDay || h.allDay || (fromMin < hTo && hFrom < toMin);
      if (!timeClash) return;
      const when = h.start === h.end ? fmtShort(h.start) : `${fmtShort(h.start)}–${fmtShort(h.end)}`;
      const time = h.allDay ? 'all day' : `${fmtTime(hFrom)}–${fmtTime(hTo)}`;
      details.push({ label: `${h.name} already off ${when} (${time})` });
    });
  });
  return details;
};

/** Reason = chip + optional note (' — ' separator; chip omitted when Other). */
export const composeReason = (chip: string, note: string): string => {
  const base = chip === 'Other' ? '' : chip;
  if (!note) return base;
  return base ? `${base} — ${note}` : note;
};

/** Whether the form can stage records (staff + date + valid time window). */
export const canStage = (form: FormState): boolean =>
  form.selectedStaffIds.length > 0 && !!form.rangeStart && (form.allDay || form.fromMin < form.toMin);

/** Staged records → batch API payload items. */
export const buildBatchPayload = (records: IStagedRecord[]): IBodyAddBlockHoursBatchItem[] =>
  records.map(r => ({
    staffId: r.staffId,
    start: r.start,
    end: r.end,
    allDay: r.allDay,
    ...(r.allDay ? {} : { fromMin: r.fromMin, toMin: r.toMin }),
    reason: r.reason,
  }));
